import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { DrawerModule } from 'primeng/drawer';
import { DrawerResizeDirective } from '../directives/drawer-resize.directive';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { MessageService } from 'primeng/api';
import {
  AddPipelineTemplatePayload,
  PipelineStage,
  PipelineTemplate,
  PipelineTemplateService,
} from './pipeline-template.service';

/** Whether the shared form is authoring a new template or editing an
 *  existing one. Derived from whether the host passed a template to
 *  edit; drives the form heading, submit label, and which service
 *  call `submit()` dispatches. */
type FormMode = 'add' | 'edit';

/** Editable stage — same shape as PipelineStage minus the id (which is
 *  server-assigned on create and preserved on edit by name-match). A
 *  local rowKey drives the @for track expression so reorders don't
 *  discard focused inputs mid-edit. */
interface EditableStage {
  /**
   * Per-row identity used as the @for track key AND the DOM id
   * suffix for stage-sla-{rowKey}, stage-terminal-{rowKey}, etc.
   * A UUID (rather than a monotonic counter) removes any risk of
   * a fresh row aliasing DOM nodes CDK drag-drop still holds
   * from a previous drawer session — a rapid close-then-reopen
   * could otherwise seed collided track keys before change
   * detection tore down the outgoing tree. UUIDs cost noise in
   * the DOM (stage-sla-{uuid}) but the ids are opaque anyway;
   * nothing external depends on their format.
   */
  rowKey: string;
  name: string;
  color: string;
  slaHours: number | null;
  requiredActions: string;
  isTerminal: boolean;
}

/** One swatch on the curated palette — the human-readable label is
 *  what a screen reader announces (aria-label previously read the raw
 *  hex string, which is unusable for keyboard / AT users). */
interface PaletteSwatch {
  hex: string;
  label: string;
}

/** Curated palette so per-template stages share the same visual
 *  vocabulary across the app (kanban headers, cards, reports).
 *  Names follow the Tailwind palette so they read naturally
 *  ("Emerald", "Amber") rather than as engineering jargon. */
const STAGE_PALETTE: readonly PaletteSwatch[] = [
  { hex: '#94a3b8', label: 'Slate' },
  { hex: '#3b82f6', label: 'Blue' },
  { hex: '#0ea5e9', label: 'Sky' },
  { hex: '#8b5cf6', label: 'Violet' },
  { hex: '#a855f7', label: 'Purple' },
  { hex: '#f59e0b', label: 'Amber' },
  { hex: '#f97316', label: 'Orange' },
  { hex: '#eab308', label: 'Yellow' },
  { hex: '#10b981', label: 'Emerald' },   // terminal-success default
  { hex: '#ef4444', label: 'Red' },       // terminal-reject default
  { hex: '#6b7280', label: 'Gray' },      // terminal-withdrawn default
];

/**
 * Add / edit form for a single pipeline template, presented as a side
 * drawer. Mirrors ManageTeamDrawer's contract exactly so the containing
 * page can drive both drawers with the same signal-based visibility
 * pattern.
 *
 * Contract (all declarative, parent-owned):
 *   - [visible] / (visibleChange): two-way open state.
 *   - [editTemplate]: the template to edit, or null for add mode. Read
 *     on open, so the parent sets it alongside flipping [visible] true.
 *   - [roster]: the current template list, used only for the
 *     duplicate-name check — the drawer never mutates or renders it.
 *   - (templateAdded) / (templateUpdated): fire on success so the page
 *     can fold the change into its own list without a reload.
 *
 * Backend note: PipelineTemplateService is stubbed against an in-memory
 * list until the recruitment API's pipeline-template endpoints land;
 * this component talks only to that service, so it needs no change
 * when the real endpoints exist.
 */
@Component({
  selector: 'lib-pipeline-template-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DrawerModule,
    DrawerResizeDirective,
    InputTextModule,
    InputNumberModule,
    CheckboxModule,
    ButtonModule,
    ChipModule,
    DragDropModule,
  ],
  templateUrl: './pipeline-template-drawer.html',
  styleUrl: './pipeline-template-drawer.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PipelineTemplateDrawer {
  private readonly messageService = inject(MessageService);
  private readonly svc            = inject(PipelineTemplateService);
  private readonly destroyRef     = inject(DestroyRef);

  readonly visible       = input.required<boolean>();
  readonly visibleChange = output<boolean>();

  readonly editTemplate = input<PipelineTemplate | null>(null);

  /** Snapshot of editTemplate captured on open — everything the save
   *  targets reads this rather than the live input so a consumer
   *  mutating editTemplate mid-edit can't retarget an in-flight save
   *  onto a different record. */
  private readonly sessionTemplate = signal<PipelineTemplate | null>(null);

  /** Current template list, supplied by the page. Used only for the
   *  duplicate-name check — never rendered here. */
  readonly roster = input<PipelineTemplate[]>([]);

  readonly templateAdded   = output<PipelineTemplate>();
  readonly templateUpdated = output<PipelineTemplate>();

  // ── Form state ─────────────────────────────────────────────────────────────
  readonly name        = signal<string>('');
  readonly description = signal<string>('');
  readonly roleTagsInput = signal<string>('');
  readonly roleTags    = signal<string[]>([]);
  readonly isDefault   = signal<boolean>(false);
  readonly stages      = signal<EditableStage[]>([]);

  readonly palette = STAGE_PALETTE;

  // rowKey is a UUID per row — no counter to maintain. See the
  // EditableStage.rowKey doc comment for the collision-avoidance
  // rationale (CDK drag-drop can still hold references across a
  // rapid close-then-reopen, and a fresh counter starting at 1
  // could otherwise alias a stale track key).

  readonly mode = computed<FormMode>(() =>
    this.sessionTemplate() ? 'edit' : 'add');

  private saveSubscription?: Subscription;
  readonly isSaving = signal(false);

  constructor() {
    // Prime the form on open, reset on close — same pattern as
    // ManageTeamDrawer. Keyed on the visibility transition (not every
    // re-render), and editTemplate is read untracked so a parent
    // updating it mid-edit doesn't retrigger this.
    let wasVisible = false;
    effect(() => {
      const visible = this.visible();
      if (visible && !wasVisible) this.prime();
      else if (!visible && wasVisible) this.reset();
      wasVisible = visible;
    });
  }

  /** True when the current name (case-insensitive, trimmed) matches
   *  another template on the roster. The template being edited is
   *  excluded so a save without a name change doesn't self-flag. */
  readonly duplicateName = computed(() => {
    const value = this.name().trim().toLowerCase();
    if (value.length === 0) return false;
    const editingId = this.sessionTemplate()?.id ?? null;
    return this.roster().some(
      t => t.id !== editingId && t.name.trim().toLowerCase() === value,
    );
  });

  /** Every reason the submit button is blocked, surfaced together so
   *  the user sees all fixes in one pass. */
  readonly blockers = computed<string[]>(() => {
    const reasons: string[] = [];
    if (this.name().trim().length === 0)      reasons.push('Give the template a name.');
    else if (this.duplicateName())            reasons.push('A template with that name already exists.');
    const stages = this.stages();
    if (stages.length < 2)                    reasons.push('Add at least two stages (a starting stage and one terminal outcome).');
    if (stages.some(s => s.name.trim().length === 0)) reasons.push('Every stage needs a name.');
    if (!stages.some(s => s.isTerminal))      reasons.push('Mark at least one stage as terminal (e.g. Hired / Rejected).');
    // Guard against duplicate stage names within the template — the
    // preserve-id-by-name-match in updateTemplate would otherwise
    // collapse two rows onto the same server-side id.
    const seenNames = new Set<string>();
    for (const s of stages) {
      const key = s.name.trim().toLowerCase();
      if (!key) continue;
      if (seenNames.has(key)) { reasons.push('Stage names must be unique within a template.'); break; }
      seenNames.add(key);
    }
    return reasons;
  });

  readonly canSave = computed(() => this.blockers().length === 0);

  onVisibleChange(visible: boolean): void {
    // reset() is owned by the visibility effect — see ManageTeamDrawer
    // for the full rationale for not double-firing here.
    this.visibleChange.emit(visible);
  }

  private close(): void {
    this.visibleChange.emit(false);
  }

  // ── Role tag chip input ────────────────────────────────────────────────────

  addRoleTag(): void {
    const raw = this.roleTagsInput().trim();
    if (!raw) return;
    // Split on commas so a paste of "sales, exec, revenue" lands as
    // three tags rather than one. Lowercase for de-dupe consistency —
    // tags are display-cased on write via a lowercase-key match.
    const incoming = raw.split(',').map(t => t.trim()).filter(Boolean);
    this.roleTags.update(existing => {
      const seen = new Set(existing.map(t => t.toLowerCase()));
      const merged = [...existing];
      for (const tag of incoming) {
        if (seen.has(tag.toLowerCase())) continue;
        seen.add(tag.toLowerCase());
        merged.push(tag);
      }
      return merged;
    });
    this.roleTagsInput.set('');
  }

  removeRoleTag(tag: string): void {
    this.roleTags.update(list => list.filter(t => t !== tag));
  }

  /** Enter in the chip input adds the current buffer as a tag instead
   *  of submitting the form. Comma also commits so a paste with a
   *  trailing comma lands cleanly. */
  onRoleTagKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.addRoleTag();
    }
  }

  // ── Stage editor ───────────────────────────────────────────────────────────

  addStage(): void {
    this.stages.update(list => [
      ...list,
      {
        rowKey: crypto.randomUUID(),
        name: '',
        color: this.palette[list.length % this.palette.length].hex,
        slaHours: null,
        requiredActions: '',
        isTerminal: false,
      },
    ]);
  }

  removeStage(rowKey: string): void {
    this.stages.update(list => list.filter(s => s.rowKey !== rowKey));
  }

  /** Reorder handler for cdk-drag-drop. cdk hands us previousIndex /
   *  currentIndex against the current array snapshot; moveItemInArray
   *  mutates in place, so we clone first to keep signal identity semantics
   *  (a downstream computed can then rely on !== to spot the change). */
  onStageDrop(event: CdkDragDrop<EditableStage[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    this.stages.update(list => {
      const next = [...list];
      moveItemInArray(next, event.previousIndex, event.currentIndex);
      return next;
    });
  }

  updateStageField<K extends keyof EditableStage>(
    rowKey: string, field: K, value: EditableStage[K],
  ): void {
    this.stages.update(list => list.map(s =>
      s.rowKey === rowKey ? { ...s, [field]: value } : s,
    ));
  }

  /**
   * Handle a mouse click on a palette swatch. Updates the stored
   * colour, then defers .focus() to the next microtask so Angular's
   * change detection has already promoted the newly-selected
   * swatch's tabindex from -1 to 0 before focus lands — otherwise
   * the roving-tabindex state is briefly inconsistent (focus on an
   * element still marked tabindex=-1), and a Tab press immediately
   * after the click could exit the group from the wrong swatch.
   * isConnected guard covers a drawer teardown between the click
   * and the microtask firing.
   */
  onPaletteClick(event: MouseEvent, stage: EditableStage, hex: string): void {
    this.updateStageField(stage.rowKey, 'color', hex);
    const target = event.currentTarget as HTMLElement;
    queueMicrotask(() => {
      if (!target.isConnected) return;
      target.focus();
    });
  }

  /**
   * Keyboard nav for the colour palette (WAI-ARIA radiogroup pattern).
   * Arrow keys move between swatches, Home/End jump to the first/last.
   * Selection follows focus — standard behaviour for a radiogroup, and
   * it keeps aria-checked in sync with the visually-focused swatch so
   * screen readers announce colour changes as the user cycles.
   *
   * Focus is moved after the signal update lands: the [attr.tabindex]
   * binding re-runs on the color change, promoting the newly-selected
   * swatch into the tab sequence — we defer the .focus() call by one
   * microtask so the DOM reflects the change before we target the
   * element. Without this deferral the not-yet-tabindex-0 button
   * would still receive focus but the roving-tabindex contract would
   * be briefly broken.
   */
  /**
   * Which swatch is currently in the tab sequence for a given stage.
   * Normally the selected swatch. When the stage's colour is off-
   * palette (a template edited elsewhere with a custom hex), the
   * first swatch takes over so the group stays reachable via Tab —
   * previously every swatch got tabindex=-1 in that case and Tab
   * skipped past the group entirely.
   */
  paletteFocusableIdx(color: string): number {
    const matched = this.palette.findIndex(p => p.hex === color);
    return matched === -1 ? 0 : matched;
  }

  /**
   * Effective colour for the aria-checked / .selected bindings.
   * EditableStage.color is typed as a non-optional string and every
   * write path defaults it, but this coalesce keeps the invariant
   * airtight at the binding layer: an undefined slipping through
   * would otherwise produce a radiogroup where every swatch reports
   * aria-checked="false" — invalid per WAI-ARIA and confusing to
   * screen readers. Falling back to palette[0] guarantees exactly
   * one swatch is checked in every render.
   */
  effectiveColor(stage: EditableStage): string {
    return stage.color || this.palette[0].hex;
  }

  /**
   * Accessible name for the palette radiogroup. Reads through
   * effectiveColor(stage) so the announced state can never diverge
   * from the aria-checked / .selected bindings — which also go
   * through effectiveColor to guarantee exactly one swatch is
   * checked. If we read raw stage.color here instead, an undefined
   * would announce "custom undefined (not in palette)" while
   * swatch 0 rendered as Emerald selected — the two disagreeing.
   */
  paletteAriaLabel(stage: EditableStage): string {
    const color = this.effectiveColor(stage);
    const matched = this.palette.find(p => p.hex === color);
    return matched
      ? `Stage colour — ${matched.label} selected`
      : `Stage colour — currently custom ${color} (not in palette)`;
  }

  onPaletteKey(event: KeyboardEvent, stage: EditableStage): void {
    // Bail on non-nav keys BEFORE any work, so Tab / typing / shortcuts
    // aren't intercepted. Tab in particular must reach the browser so
    // roving-tabindex hands focus to the next control outside the group.
    const NAV_KEYS = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'];
    if (!NAV_KEYS.includes(event.key)) return;
    event.preventDefault();

    // Read the latest colour from the signal, NOT the closure argument.
    // The template's (keydown) closure captures a stage snapshot from
    // the @for iteration in which the row was last rendered — rapid
    // successive keypresses fire before Angular re-renders the row
    // with the updated stages(), so every keydown after the first
    // would compute currentIdx from the same stale colour and jump
    // back to the original starting swatch instead of advancing
    // sequentially. Falling back to the closure colour covers a
    // (very unlikely) case where the row has already been removed
    // between the keydown and this handler.
    const latest = this.stages().find(s => s.rowKey === stage.rowKey);
    const color  = latest?.color ?? stage.color;

    const currentIdx = this.palette.findIndex(p => p.hex === color);
    let nextIdx: number;

    if (currentIdx === -1) {
      // Off-palette anchor: pick the natural landing per key rather
      // than forcing everything to swatch 0. End on an off-palette
      // stage should still jump to the last swatch; Left/Up should
      // wrap to the last too (from "nothing" ⇒ end of list); Right/
      // Down/Home go to the first. The previous unconditional
      // nextIdx=0 sent End to the wrong place.
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'Home':
          nextIdx = 0;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'End':
          nextIdx = this.palette.length - 1;
          break;
        default:
          return; // Unreachable — NAV_KEYS guarded above.
      }
    } else {
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          nextIdx = (currentIdx + 1) % this.palette.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIdx = (currentIdx - 1 + this.palette.length) % this.palette.length;
          break;
        case 'Home':
          nextIdx = 0;
          break;
        case 'End':
          nextIdx = this.palette.length - 1;
          break;
        default:
          return; // Unreachable — NAV_KEYS guarded above.
      }
    }

    if (nextIdx === currentIdx) return;

    this.updateStageField(stage.rowKey, 'color', this.palette[nextIdx].hex);

    const container = event.currentTarget as HTMLElement;
    queueMicrotask(() => {
      // Bail if the container was detached between the keydown and
      // this microtask — the drawer can be closed (or the row
      // removed) mid-event. On a detached node, querySelectorAll
      // works but element.focus() is a silent no-op in most
      // browsers; the isConnected check makes the intent explicit
      // and skips the DOM query entirely.
      if (!container.isConnected) return;
      const swatches = container.querySelectorAll<HTMLElement>('.palette-swatch');
      swatches[nextIdx]?.focus();
    });
  }

  // ── Submit ─────────────────────────────────────────────────────────────────

  submit(): void {
    if (!this.canSave() || this.isSaving()) return;

    // Flush any uncommitted tag typing so a user who typed 'sales'
    // in the chip input and clicked Save without pressing Enter /
    // comma doesn't silently lose the tag. addRoleTag handles
    // dedupe + trim + roleTagsInput reset internally, so calling
    // it here is safe when the buffer's already empty (early-return).
    this.addRoleTag();

    const payload: AddPipelineTemplatePayload = {
      name:        this.name().trim(),
      description: this.description().trim(),
      roleTags:    this.roleTags(),
      isDefault:   this.isDefault(),
      stages: this.stages().map<Omit<PipelineStage, 'id'>>(s => ({
        name:            s.name.trim(),
        color:           s.color,
        slaHours:        s.slaHours ?? undefined,
        // Split checklist textarea on newlines; blank lines drop.
        requiredActions: s.requiredActions.split(/\r?\n/).map(l => l.trim()).filter(Boolean),
        isTerminal:      s.isTerminal,
      })),
    };

    const template = this.sessionTemplate();
    if (template) this.saveEdit(template.id, payload);
    else this.saveAdd(payload);
  }

  // isSaving is cleared explicitly in the next / error callbacks below
  // rather than in finalize. finalize would also fire on either path,
  // but the visibility effect (which reads isSaving to decide whether
  // to emit the 'Save cancelled' toast) can run between the callback
  // and finalize on the success path — so isSaving must already be
  // false before close() emits. Setting it in both places produced a
  // redundant signal write and a shutdown sequence that was harder to
  // reason about; keeping the state transitions on the same code path
  // as the toast/close side effects makes the ordering explicit.
  // finalize is left as the single place to null the subscription
  // reference, which is idempotent and unrelated to signal state.

  private saveAdd(payload: AddPipelineTemplatePayload): void {
    this.saveSubscription?.unsubscribe();
    this.isSaving.set(true);
    this.saveSubscription = this.svc.addTemplate(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { this.saveSubscription = undefined; }),
      )
      .subscribe({
        next: created => {
          this.messageService.add({
            severity: 'success',
            summary:  'Template added',
            detail:   `"${created.name}" is ready to use.`,
          });
          this.templateAdded.emit(created);
          this.isSaving.set(false);
          this.close();
        },
        error: () => {
          this.isSaving.set(false);
          this.messageService.add({
            severity: 'error',
            summary:  'Add failed',
            detail:   'Could not add the template. Please try again.',
          });
        },
      });
  }

  private saveEdit(id: string, payload: AddPipelineTemplatePayload): void {
    this.saveSubscription?.unsubscribe();
    this.isSaving.set(true);
    this.saveSubscription = this.svc.updateTemplate(id, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { this.saveSubscription = undefined; }),
      )
      .subscribe({
        next: updated => {
          this.messageService.add({
            severity: 'success',
            summary:  'Changes saved',
            detail:   `Updated "${updated.name}".`,
          });
          this.templateUpdated.emit(updated);
          this.isSaving.set(false);
          this.close();
        },
        error: () => {
          this.isSaving.set(false);
          this.messageService.add({
            severity: 'error',
            summary:  'Save failed',
            detail:   'Could not save the changes. Please try again.',
          });
        },
      });
  }

  // ── Prime / reset ──────────────────────────────────────────────────────────

  private prime(): void {
    const template = untracked(this.editTemplate);
    this.sessionTemplate.set(template);
    if (template) {
      this.name.set(template.name);
      this.description.set(template.description);
      this.roleTags.set([...template.roleTags]);
      this.roleTagsInput.set('');
      this.isDefault.set(template.isDefault);
      this.stages.set(template.stages.map(s => ({
        rowKey:          crypto.randomUUID(),
        name:            s.name,
        // Default undefined colours to the first swatch, but PRESERVE
        // a defined off-palette colour (e.g. a template edited via
        // API with a custom hex, or a legacy record). Snapping to
        // palette[0] would be a silent destructive write on the next
        // save even if the user made no colour changes. AT users
        // still hear the current colour via paletteAriaLabel(),
        // which describes the state whether or not a swatch matches.
        color:           s.color ?? this.palette[0].hex,
        slaHours:        s.slaHours ?? null,
        requiredActions: s.requiredActions.join('\n'),
        isTerminal:      s.isTerminal,
      })));
    } else {
      this.resetForm();
      // Seed a sensible starting shape so a first-time user sees the
      // structure they need to fill in, not an empty rectangle.
      this.stages.set([
        { rowKey: crypto.randomUUID(), name: 'Sourced',    color: this.palette[0].hex, slaHours: 48,  requiredActions: '', isTerminal: false },
        { rowKey: crypto.randomUUID(), name: 'Interview',  color: this.palette[3].hex, slaHours: 168, requiredActions: '', isTerminal: false },
        { rowKey: crypto.randomUUID(), name: 'Hired',      color: this.palette[8].hex, slaHours: null, requiredActions: '', isTerminal: true },
      ]);
    }
  }

  private resetForm(): void {
    this.name.set('');
    this.description.set('');
    this.roleTags.set([]);
    this.roleTagsInput.set('');
    this.isDefault.set(false);
    this.stages.set([]);
    // rowKey is now a UUID per row — no counter to reset.
  }

  private reset(): void {
    // Cancel any in-flight save silently. The prior "Save cancelled"
    // toast fired on every close-while-saving, including the transient
    // close the parent page uses to re-open the drawer against a
    // different template — the user would then see a spurious toast
    // for a save they didn't actually cancel. Explicit dismiss
    // (Escape, close button, backdrop) still cancels the save; it
    // just does so quietly, which matches how most drawer/dialog
    // patterns handle "close during in-flight work" — the request is
    // abandoned and the UI moves on without narrating the abort.
    this.saveSubscription?.unsubscribe();
    this.saveSubscription = undefined;
    this.isSaving.set(false);
    this.sessionTemplate.set(null);
    this.resetForm();
  }
}
